import { getSubComments, onPostReaction, postReactionComment, postSubmitComment } from "api-function/postContentApi";
import { requestSubmitFollowingUser } from "api-function/requestFriendStatusApi";
import { PostReaction } from "models/postReaction";
import { AppLog } from "services";
import { AppRecordJK, AppRecordStatusJK, RequestApi, RouteApi, StatusMsg } from "utils";
import { MessageType } from "utils/enum/message_type";
import { Service } from "utils/services";
import { create } from "zustand";
import socket from "services/socket";
import useSocketStore from "./useSocketStore";
import appStore from "./appStore";

interface ViewPostContentState {
  posts: any[];
  postsRecord: AppRecordJK;
  currentIndex: number;
  comments: any[];
  commentsRecord: AppRecordJK;
  currentPost: any;

  initializePage: (postId: string) => Promise<void>;
  disposedPage: () => Promise<void>;
  fetchProfilePostContent: (postId: string, isRefresh: boolean) => Promise<void>;

  onHandleUp: () => Promise<void>;
  onHandleDown: () => Promise<void>;
  setComments: () => Promise<void>;
  fetchComments: () => Promise<void>;
  requestReactionComment: (comment: any) => Promise<void>;
  onUpdateReactionLike: (comments: any[], targetId: string, newSubComment: any) => any[];
  requestReplyComment: ({
    replyId,
    replyText,
    headerId,
    commentId,
    commentUrl,
  }: {
    replyId: string;
    replyText: string;
    headerId: string;
    commentId: string;
    commentUrl: string;
  }) => Promise<void>;
  onAddOrUpdateSubComment(comments: any[], targetId: string, headerId: string, newSubComment: any, addNew: boolean): any[];
  requestSubmitComment: (sticker: any, textComment: string) => Promise<void>;
  fetchSubComments: (commentId: string, nextPageUrl?: string) => Promise<void>;

  onClickedLike: (post: any) => Promise<void>;
  onClickedShare: (post: any) => Promise<void>;
  onClickedFollow: (post: any, userId: any) => Promise<void>;
  onClickedFavorite: (post: any) => Promise<void>;

  commentSocket: () => void;
}

export const useViewPostContentStore = create<ViewPostContentState>((set, get) => ({
  posts: [],
  postsRecord: new AppRecordJK(),
  currentIndex: 0,
  comments: [],
  commentsRecord: new AppRecordJK(),
  currentPost: null,

  initializePage: async (postId: string) => {
    try {
      set({
        posts: [],
        postsRecord: new AppRecordJK(),
        currentIndex: 0,
        comments: [],
        commentsRecord: new AppRecordJK(),
        currentPost: null,
      });

      await useViewPostContentStore
        .getState()
        .fetchProfilePostContent(postId, true)
        .then(async (_) => {
          await useViewPostContentStore.getState().setComments();
          await useViewPostContentStore.getState().commentSocket();

          const { joinRoom } = useSocketStore.getState();
          joinRoom(postId);
        });
    } catch (error) {
      AppLog.show({
        message: `[initializePage]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  disposedPage: async () => {
    try {
      const { currentPost } = get();
      const { leaveRoom } = useSocketStore.getState();
      if (currentPost) leaveRoom(`${currentPost?.provider_post_id}`);
      socket.off("onFlipFlopComment", () => {});

      set({
        posts: [],
        postsRecord: new AppRecordJK(),
        currentIndex: 0,
        comments: [],
        commentsRecord: new AppRecordJK(),
        currentPost: null,
      });
    } catch (error) {
      AppLog.show({
        message: `[disposedPage]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  fetchProfilePostContent: async (postId: string, isRefresh: boolean) => {
    const { posts, postsRecord } = get();
    let tmpPosts = [...posts];
    let tmpPostsRecord = new AppRecordJK({
      ...postsRecord,
    });

    try {
      if (isRefresh) {
        tmpPostsRecord.loadingStatus = AppRecordStatusJK.loading;
        set({ postsRecord: tmpPostsRecord });
      } else {
        if (tmpPostsRecord.canLoadMore()) {
          tmpPostsRecord.loadingStatus = AppRecordStatusJK.loadingMore;
          set({ postsRecord: tmpPostsRecord });
        } else {
          return;
        }
      }

      let url: string = "";
      if (Service.isAuthenticated) {
        url = RouteApi.getHomeProfilePost;
        url += `token=${Service.token}`;
      } else {
        url = RouteApi.getProfilePostContentsPageView;
      }

      const response = await RequestApi(url, {
        data: { post_id: postId, direction: isRefresh ? "" : "down" },
      });

      if (response.status === "success") {
        const newPosts: any[] = response.posts;

        if (newPosts && newPosts.length > 0) {
          tmpPostsRecord.loadingStatus = AppRecordStatusJK.loaded;
        } else {
          tmpPostsRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        }

        if (isRefresh) {
          set({
            postsRecord: tmpPostsRecord,
            posts: [...tmpPosts, ...newPosts],
            currentPost: { ...newPosts[0] },
          });
        } else {
          set({
            postsRecord: tmpPostsRecord,
            posts: [...tmpPosts, ...newPosts],
          });
        }
      } else {
        tmpPostsRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        set({
          postsRecord: tmpPostsRecord,
        });
        AppLog.show({
          message: `[fetchProfilePostContent] ${response["msg"]}`,
          status: StatusMsg.getType(response["status"]),
        });
      }
    } catch (error) {
      tmpPostsRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
      set({
        postsRecord: tmpPostsRecord,
      });

      AppLog.show({
        message: `[fetchProfilePostContent]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onHandleUp: async () => {
    try {
      const { currentIndex, posts, currentPost } = get();
      const { joinRoom, leaveRoom } = useSocketStore.getState();
      if (posts.length > 0 && currentIndex >= 0) {
        const tmpCurrentIndex = currentIndex - 1;

        // Socket
        joinRoom(`${posts[tmpCurrentIndex]?.provider_post_id}`);
        if (currentPost) leaveRoom(`${currentPost?.provider_post_id}`);

        set({
          currentIndex: tmpCurrentIndex,
          currentPost: posts[tmpCurrentIndex],
        });

        window.history.pushState({}, "", `/post?post_id=${posts[tmpCurrentIndex].post_id}`);
        await useViewPostContentStore.getState().setComments();
      }
    } catch (error) {
      AppLog.show({
        message: `[onHandleDown]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onHandleDown: async () => {
    try {
      const { currentIndex, posts, currentPost } = get();
      const { joinRoom, leaveRoom } = useSocketStore.getState();

      if (posts.length > 0 && currentIndex < posts.length - 1) {
        const tmpCurrentIndex = currentIndex + 1;

        // Socket
        joinRoom(`${posts[tmpCurrentIndex]?.provider_post_id}`);
        if (currentPost) leaveRoom(`${currentPost?.provider_post_id}`);

        set({
          currentIndex: tmpCurrentIndex,
          currentPost: posts[tmpCurrentIndex],
        });

        window.history.pushState({}, "", `/post?post_id=${posts[tmpCurrentIndex].post_id}`);
        await useViewPostContentStore.getState().setComments();

        if (posts.length - currentIndex === 2) {
          await useViewPostContentStore.getState().fetchProfilePostContent(posts[posts.length - 1]?.post_id, false);
        }
      }
    } catch (error) {
      AppLog.show({
        message: `[onHandleDown]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  setComments: async () => {
    const { posts, commentsRecord, currentPost } = get();

    let tmpCommentsRecord = new AppRecordJK({
      ...commentsRecord,
    });

    try {
      if (posts.length > 0) {
        tmpCommentsRecord.loadingStatus = AppRecordStatusJK.loading;
        set({ commentsRecord: tmpCommentsRecord });

        const comments = currentPost.comments;

        if (comments.next_page_url) {
          tmpCommentsRecord.loadingStatus = AppRecordStatusJK.loaded;
        } else {
          tmpCommentsRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        }
        tmpCommentsRecord.nextPageUrl = comments.next_page_url;

        set({
          commentsRecord: tmpCommentsRecord,
          comments: [...comments.data],
        });
      }
    } catch (error) {
      tmpCommentsRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
      set({ commentsRecord: tmpCommentsRecord });

      AppLog.show({
        message: `[setComments]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  fetchComments: async () => {
    const { commentsRecord, comments } = get();

    let tmpComments = [...comments];
    let tmpCommentsRecord = new AppRecordJK({
      ...commentsRecord,
    });

    try {
      let url: string = "";
      if (tmpCommentsRecord.canLoadMore()) {
        tmpCommentsRecord.loadingStatus = AppRecordStatusJK.loadingMore;
        set({ commentsRecord: tmpCommentsRecord });

        if (tmpCommentsRecord.nextPageUrl) {
          url = tmpCommentsRecord.nextPageUrl;
        } else {
          return;
        }
      } else {
        return;
      }

      const response = await RequestApi(url);
      if (response.status === "success") {
        const newComments = response.comments;

        if (newComments.next_page_url) {
          tmpCommentsRecord.loadingStatus = AppRecordStatusJK.loaded;
        } else {
          tmpCommentsRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        }
        tmpCommentsRecord.nextPageUrl = newComments.next_page_url;

        set({
          commentsRecord: tmpCommentsRecord,
          comments: [...tmpComments, ...newComments.data],
        });
      } else {
        tmpCommentsRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        set({
          commentsRecord: tmpCommentsRecord,
        });
        AppLog.show({
          message: response["msg"],
          status: StatusMsg.getType(response["status"]),
        });
      }
    } catch (error) {
      tmpCommentsRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
      set({ commentsRecord: tmpCommentsRecord });

      AppLog.show({
        message: `[fetchComments]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  requestReactionComment: async ({ comment }: { comment: any }) => {
    const { comments } = get();
    try {
      let tmpComment = comment;
      const isLike = tmpComment["is_like"]?.toString() === "0" ? "1" : "0";
      const data = {
        comment_id: tmpComment["comment_id"]?.toString(),
        is_like: isLike,
        key_remove_id: await Service.getSynchID(),
      };

      const updatedComments = get().onUpdateReactionLike(comments, tmpComment["comment_id"].toString(), data);
      set({ comments: [...updatedComments] });

      const result = await postReactionComment({ data: data });
      if (result.status === StatusMsg.success.value) {
      } else {
        AppLog.show({
          message: `Error ${result["msg"]}`,
          status: StatusMsg.success,
        });
      }
    } catch (err) {
      AppLog.show({
        message: `Error Catch requestReactionComment ${err}`,
        status: StatusMsg.error,
      });
    }
  },

  onUpdateReactionLike: (comments: any[], targetId: string, newSubComment: any) => {
    return comments.map((comment: any) => {
      if (comment.comment_id.toString() === targetId) {
        return {
          ...comment,
          is_like: newSubComment["is_like"],
          key_remove_id: newSubComment["key_remove_id"],
        };
      }
      if (comment.sub_comments?.data?.length) {
        return {
          ...comment,
          sub_comments: {
            ...comment.sub_comments,
            data: get().onUpdateReactionLike(comment.sub_comments.data, targetId, newSubComment),
          },
        };
      }
      return comment;
    });
  },

  requestReplyComment: async ({
    replyId,
    replyText,
    headerId,
    commentId,
    commentUrl,
  }: {
    replyId: string;
    replyText: string;
    headerId: string;
    commentId: string;
    commentUrl: string;
  }) => {
    const { currentPost, comments } = get();

    try {
      const comment = {
        user_id: currentPost?.user_id,
        reply_comment_id: replyId,
        comment: replyText,
        comment_type: commentUrl === "" ? MessageType.text.value : MessageType.sticker.value,
        comment_url: commentUrl,
        post_id: currentPost?.provider_post_id.toString(),
        attachment_id: currentPost?.post_attachments[0].attachment_id.toString(),
        key_remove_id: await Service.getSynchID(),
      };

      const tmpSubComment = {
        id: 0,
        user_id: Service?.userAuth["id"].toString(),
        comment_id: commentId,
        sender: {
          id: Service?.userAuth["id"].toString(),
          email: Service?.userAuth["email"].toString(),
          username: Service?.userAuth["username"].toString(),
          name: Service?.userAuth["name"].toString(),
          gender: Service?.userAuth["gender"].toString(),
          picture: Service?.userAuth["picture"].toString(),
          thumbnail: Service?.userAuth["thumbnail"].toString(),
        },
        reply_comment_id: comment["reply_comment_id"],
        comment: replyText,
        comment_datetime: new Date().toISOString(), // Convert date to string
        sending: true,
        key_remove_id: comment["key_remove_id"],
      };
      const updatedComments = get().onAddOrUpdateSubComment(comments, replyId, headerId, tmpSubComment, true);
      set({ comments: [...updatedComments] });

      const result = await postSubmitComment({ comment: comment });
      if (result.status === StatusMsg.success.value) {
      } else {
      }
    } catch (err) {
      AppLog.show({
        message: `ERROR CATCH ${err}`,
        status: StatusMsg.error,
      });
    }
  },

  onAddOrUpdateSubComment(comments: any[], targetId: string, headerId: string, newSubComment: any, addNew: boolean): any[] {
    return comments.map((comment: any) => {
      if (comment.id.toString() === targetId) {
        if (!comment.sub_comments || !comment.sub_comments.data) {
          return {
            ...comment,
            sub_comments: {
              data: [newSubComment],
            },
          };
        }
        const tmpSubComment = [...comment.sub_comments.data];
        const index = tmpSubComment.findIndex((item: any) => item["key_remove_id"]?.toString() === headerId);
        if (index !== -1) {
          tmpSubComment[index] = {
            ...tmpSubComment[index],
            ...newSubComment,
          };
          return {
            ...comment,
            sub_comments: {
              ...comment.sub_comments,
              data: tmpSubComment,
            },
          };
        } else {
          return {
            ...comment,
            sub_comments: {
              ...comment.sub_comments,
              data: [newSubComment, ...tmpSubComment],
            },
          };
        }
      }
      if (comment.sub_comments?.data?.length) {
        return {
          ...comment,
          sub_comments: {
            ...comment.sub_comments,
            data: get().onAddOrUpdateSubComment(comment.sub_comments.data, targetId, headerId, newSubComment, addNew),
          },
        };
      }

      return comment;
    });
  },

  requestSubmitComment: async (sticker: any, textComment: string) => {
    if (Service.isAuthenticated === false) {
      appStore.getState().onOpenDialogWelcome();
      return;
    }

    const { comments, currentPost } = get();

    try {
      const commentType = Object.keys(sticker).length === 0 ? MessageType.text.value : MessageType.sticker.value;
      const comment = {
        user_id: currentPost?.user_id,
        reply_comment_id: "",
        comment: textComment,
        comment_type: commentType,
        comment_url: Object.keys(sticker).length === 0 ? "" : sticker.thumbnail_url,
        post_id: currentPost?.provider_post_id.toString(),
        attachment_id: currentPost?.post_attachments[0].attachment_id.toString(),
        key_remove_id: await Service.getSynchID(),
      };
      const tmpComment = {
        id: 0,
        is_sending: true,
        user_id: Service?.userAuth["id"].toString(),
        sender: {
          id: Service?.userAuth["id"].toString(),
          email: Service?.userAuth["email"].toString(),
          username: Service?.userAuth["username"].toString(),
          name: Service?.userAuth["name"].toString(),
          gender: Service?.userAuth["gender"].toString(),
          picture: Service?.userAuth["picture"].toString(),
          thumbnail: Service?.userAuth["thumbnail"].toString(),
        },
        reply_comment_id: comment["reply_comment_id"],
        comment: comment["comment"],
        comment_datetime: new Date().toISOString(),
        sending: true,
        key_remove_id: comment["key_remove_id"],
      };
      let newComments = [...comments];
      newComments.unshift(tmpComment);
      set({ comments: [...newComments] });
      const result = await postSubmitComment({ comment: comment });
      if (result.status === StatusMsg.success.value) {
      } else {
        AppLog.show({
          message: `Error ${result["msg"]}`,
          status: StatusMsg.success,
        });
      }
    } catch (error) {
      AppLog.show({
        message: `[requestSubmitComment]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  fetchSubComments: async (commentId: string, nextPageUrl?: string) => {
    const { comments, currentPost } = get();

    try {
      const result = await getSubComments({
        postId: currentPost?.provider_post_id,
        attachmentId: currentPost?.post_attachments[0]["attachment_id"],
        replyCommentId: commentId,
        nextPageUrl: nextPageUrl,
      });

      if (result.status === "success") {
        const fetchedSubComments = {
          data: [...result["comments"]["data"]],
          next_page_url: result["comments"]["next_page_url"],
        };

        const updateCommentWithSubComments = (comments: any, targetId: string, subComments: any) => {
          return comments.map((comment: any) => {
            if (comment.id.toString() === targetId) {
              const existingSubComments = comment.sub_comments?.data || [];
              const mergedSubComments = Array.from(new Map([...existingSubComments, ...subComments.data].map((sc) => [sc.id, sc])).values());

              const updatedSubComments = {
                ...subComments,
                data: mergedSubComments,
              };

              return { ...comment, sub_comments: updatedSubComments };
            }

            if (comment.sub_comments?.data?.length) {
              return {
                ...comment,
                sub_comments: {
                  ...comment.sub_comments,
                  data: updateCommentWithSubComments(comment.sub_comments.data, targetId, subComments),
                },
              };
            }

            return comment;
          });
        };
        const updatedComments = updateCommentWithSubComments(comments, commentId, fetchedSubComments);
        set({ comments: [...updatedComments] });
      } else {
        AppLog.show({
          message: `Failed to fetch sub-comments: ${result["msg"]}`,
        });
      }
    } catch (error) {
      AppLog.show({
        message: `[fetchSubComments]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onClickedLike: async (post: any) => {
    try {
      if (Service.isAuthenticated === false) {
        appStore.getState().onOpenDialogWelcome();
        return;
      }

      const { posts } = get();
      if (Service.userAuth) {
        const attachment: any = post?.post_attachments[0];
        const isLike: boolean = !Boolean(Service.toInteger(post?.is_reaction_like));

        const responses = await onPostReaction({
          content: post,
          reaction: PostReaction.like,
          isLike: isLike,
          isFavorite: false,
          attachment,
        });
        if (responses.status === "success") {
          let tmpPost = structuredClone(post);
          tmpPost.is_reaction_like = isLike ? "1" : "0";
          const numberLike = isLike ? 1 : -1;
          tmpPost.reaction_like_count = Service.toInteger(tmpPost.reaction_like_count) + numberLike;

          const editIndex = posts.findIndex((post) => post.provider_post_id === tmpPost.provider_post_id);
          if (editIndex !== -1) {
            console.log("editIndex");
            set((state) => {
              const updatedPosts = [...state.posts];
              updatedPosts[editIndex] = tmpPost;
              return { posts: updatedPosts };
            });
          }
        } else {
          console.log("error");
        }
      }
    } catch (error) {
      AppLog.show({
        message: `[onClickedLike]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onClickedShare: async (post: any) => {
    try {
      if (Service.isAuthenticated === false) {
        appStore.getState().onOpenDialogWelcome();
        return;
      }

      const { posts } = get();
      const tmpPost = { ...post };

      const editIndex = posts.findIndex((post) => post.provider_post_id === tmpPost.provider_post_id);
      if (editIndex !== -1) {
        set((state) => {
          const updatedPosts = [...state.posts];
          updatedPosts[editIndex] = tmpPost;
          return { posts: updatedPosts };
        });
      }
    } catch (error) {
      AppLog.show({
        message: `[onClickedShare]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onClickedFollow: async (post: any, userId: string) => {
    try {
      if (Service.isAuthenticated === false) {
        appStore.getState().onOpenDialogWelcome();
        return;
      }

      const { posts } = get();

      const responses = await requestSubmitFollowingUser({ userId: userId });
      if (responses.status === "success") {
        let tmpPost = structuredClone(post);

        if (tmpPost.re_post_user.id.toString() === userId) {
          tmpPost.re_post_user.friend_status = responses.user?.friend_status ?? null;
        }
        const editIndex = posts.findIndex((post) => post.provider_post_id === tmpPost.provider_post_id);
        if (editIndex !== -1) {
          set((state) => {
            const updatedPosts = [...state.posts];
            updatedPosts[editIndex] = tmpPost;
            return { posts: updatedPosts };
          });
        }
      } else {
      }
    } catch (error) {
      AppLog.show({
        message: `[onClickedFollow]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onClickedFavorite: async (post: any) => {
    try {
      if (Service.isAuthenticated === false) {
        appStore.getState().onOpenDialogWelcome();
        return;
      }

      const { posts } = get();
      if (Service.userAuth) {
        const attachment: any = post?.post_attachments[0];
        const isFavorite: boolean = !Boolean(Service.toInteger(post?.is_reaction_favorite));

        const responses = await onPostReaction({
          content: post,
          reaction: PostReaction.favorite,
          isLike: false,
          isFavorite: isFavorite,
          attachment,
        });

        if (responses.status === "success") {
          let tmpPost = structuredClone(post);
          tmpPost.is_reaction_favorite = isFavorite ? "1" : "0";
          const numberFavorite = isFavorite ? 1 : -1;
          tmpPost.reaction_favorite_count = Service.toInteger(tmpPost.reaction_favorite_count) + numberFavorite;

          const editIndex = posts.findIndex((post) => post.provider_post_id === tmpPost.provider_post_id);
          if (editIndex !== -1) {
            set((state) => {
              const updatedPosts = [...state.posts];
              updatedPosts[editIndex] = tmpPost;
              return { posts: updatedPosts };
            });
          }
        } else {
        }
      }
    } catch (error) {
      AppLog.show({
        message: `[onClickedFavorite]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  commentSocket: () => {
    socket.on("onFlipFlopComment", (result: any) => {
      const { comments, currentPost } = get();

      let tmpComments = [...comments];
      let tmpPost = { ...currentPost };

      const postComments = [...result["post_comments"]];

      if (result["flag"] === "added") {
        postComments.forEach((postComment) => {
          if (`${currentPost?.provider_post_id}` === `${postComment?.post_id}`) {
            const keyRemoveId = postComment["key_remove_id"]?.toString();
            const replyCommentId = postComment["reply_comment_id"]?.toString();
            if (replyCommentId === "0" || replyCommentId === "") {
              const index = tmpComments.findIndex((item: any) => item["key_remove_id"]?.toString() === keyRemoveId);
              if (index !== -1) {
                tmpComments[index] = { ...postComment };
              } else {
                tmpComments = [postComment, ...tmpComments];
              }
              tmpPost.comments.data = [...tmpComments];
              set({ comments: tmpComments, currentPost: tmpPost });
            } else {
              const resposeComment = postComment;
              const updateResponseComment = get().onAddOrUpdateSubComment(tmpComments, replyCommentId, keyRemoveId, resposeComment, false);
              tmpPost.comments.data = [...tmpComments];
              set({ comments: updateResponseComment, currentPost: tmpPost });
            }
          }
        });
      } else if (result["flag"] === "liked") {
        if (result["reaction_user_id"]?.toString() === Service.userAuth["id"].toString()) {
          const updatedComments = get().onUpdateReactionLike(tmpComments, result["comment_id"], postComments[0]);
          tmpComments = updatedComments;
          tmpPost.comments.data = [...tmpComments];
          set({ comments: updatedComments, currentPost: tmpPost });
        } else {
          console.log("No target");
        }
      }
    });
  },
}));

